export interface PackageMaxDims {
  maxPackageLength: number | null | undefined;
  maxPackageWidth: number | null | undefined;
  maxPackageHeight: number | null | undefined;
}

export interface PackageItemDims {
  length: number | null | undefined;
  width: number | null | undefined;
  height: number | null | undefined;
}

export type PackageDimensionAxis = "length" | "width" | "height";

export interface PackageDimensionError {
  index: number;
  axis: PackageDimensionAxis;
}

/**
 * Resolve the vehicle's max horizontal dims into a normalized
 * `[effMaxLongSide, effMaxShortSide]` tuple. Mirror of the backend
 * `resolveEffectiveLW` helper.
 *
 * Rules:
 * - Both null -> [null, null] (no L/W check applies).
 * - Only one set -> the unset one falls through to the set one.
 * - Both set -> always normalized so the larger cap comes first. The labels
 *   `maxPackageLength` / `maxPackageWidth` on the vehicle are treated as an
 *   unordered pair (the admin may have entered them swapped).
 */
export function resolveEffectiveLW(
  maxL: number | null | undefined,
  maxW: number | null | undefined
): [number | null, number | null] {
  const rawL = maxL ?? maxW ?? null;
  const rawW = maxW ?? maxL ?? null;
  if (rawL == null || rawW == null) return [rawL, rawW];
  return [Math.max(rawL, rawW), Math.min(rawL, rawW)];
}

interface ValidateArgs {
  vehicleMaxDims: PackageMaxDims;
  hasVehicleSelected: boolean;
  packageItems: ReadonlyArray<PackageItemDims> | null | undefined;
}

/**
 * Returns one error per (row, axis) violation. Returns [] when validation
 * does not apply (no vehicle selected, no package items, or vehicle has all
 * three caps null).
 *
 * Mirrors the rules in `PackageDimensionsValidator.validate` on the backend
 * with one intentional UX divergence: when both the long side AND short side
 * of a row exceed their respective caps, the frontend surfaces BOTH errors
 * so every violating input renders red simultaneously. The backend's
 * `require(...)` calls throw on the first violation; that's fine as
 * defense-in-depth on the API surface, but a two-step reveal is poor
 * dispatcher UX.
 */
export function validatePackageDimensions({
  vehicleMaxDims,
  hasVehicleSelected,
  packageItems,
}: ValidateArgs): PackageDimensionError[] {
  if (!hasVehicleSelected) return [];
  if (!packageItems || packageItems.length === 0) return [];

  const [effMaxL, effMaxW] = resolveEffectiveLW(
    vehicleMaxDims.maxPackageLength,
    vehicleMaxDims.maxPackageWidth
  );
  const maxH = vehicleMaxDims.maxPackageHeight ?? null;

  if (effMaxL == null && effMaxW == null && maxH == null) return [];

  const errors: PackageDimensionError[] = [];

  packageItems.forEach((item, index) => {
    const l = item.length ?? null;
    const w = item.width ?? null;
    const h = item.height ?? null;

    if (l != null && w != null) {
      const longSide = Math.max(l, w);
      const shortSide = Math.min(l, w);

      if (effMaxL != null && longSide > effMaxL) {
        errors.push({ index, axis: l >= w ? "length" : "width" });
      }
      if (effMaxW != null && shortSide > effMaxW) {
        errors.push({ index, axis: l <= w ? "length" : "width" });
      }
    } else if (l != null || w != null) {
      // Single supplied side is the package's only known horizontal dim; it
      // must fit through the larger cap regardless of orientation. effMaxW
      // can't be enforced because we don't know the missing axis.
      const singleSide = (l ?? w) as number;
      if (effMaxL != null && singleSide > effMaxL) {
        errors.push({ index, axis: l != null ? "length" : "width" });
      }
    }

    if (maxH != null && h != null && h > maxH) {
      errors.push({ index, axis: "height" });
    }
  });

  return errors;
}
